import axios from 'axios';

/**
 * HubSpot Service Integration
 * 
 * This service integrates with the backend HubSpot API endpoints according to the provided API documentation.
 * 
 * Key API Mappings:
 * - GET /hubspot/contact?email={email} - Search contact by email
 * - POST /hubspot/contact - Create new contact (expects: first_name, last_name, email, phone?)
 * - GET /hubspot/contacts?limit={limit}&after={after} - Get all contacts with pagination
 * - POST /hubspot/engagement - Create engagement (expects: engagement_type, notes, contact_id, customer_id)
 * - GET /hubspot/contact/{contact_id}/engagements - Get contact engagements
 * - GET /customer/{customer_id}/hubspot/engagements - Get customer's engagements with full details
 * - POST /customer/{customer_id}/hubspot/quick-add - Manually associate existing engagement with customer
 * 
 * Special Notes:
 * - The customer_id parameter is required when creating engagements to properly associate them with customers
 * - If an engagement is created without proper customer association, use the quick-add endpoint to link them
 * 
 * All endpoints require JWT authentication via Authorization: Bearer {token} header.
 */

// TypeScript-like interfaces documented for reference (implement runtime validation if needed)
/*
interface ContactCreate {
  first_name: string;
  last_name: string;
  email: string;
  phone?: string;
}

interface EngagementCreate {
  engagement_type: string;
  notes: string;
  contact_id: string;
  customer_id?: string;
}

interface HubSpotContact {
  id: string;
  properties: {
    firstname: string;
    lastname: string;
    email: string;
    phone?: string;
    createdate: string;
    lastmodifieddate: string;
  };
  createdAt?: string;
  updatedAt?: string;
}

interface HubSpotEngagement {
  engagement: {
    id: number;
    type: string;
    timestamp: number;
  };
  metadata: {
    title: string;
    body: string;
    startTime?: number;
    endTime?: number;
  };
}

interface ApiResponse<T> {
  status: "success" | "not_found";
  data?: T;
  message?: string;
}

interface PaginatedResponse<T> {
  results: T[];
  paging?: {
    next?: {
      after: string;
      link: string;
    };
  };
}
*/

class HubSpotService {
  constructor(apiBaseUrl, getAuthToken) {
    this.apiBaseUrl = apiBaseUrl;
    this.getAuthToken = getAuthToken;
  }

  async request(endpoint, options = {}) {
    const token = this.getAuthToken();
    if (!token) {
      throw new Error('Authentication token not available');
    }

    const response = await axios({
      url: `${this.apiBaseUrl}/hubspot${endpoint}`,
      ...options,
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    return response.data;
  }

  // Search for contact by email
  async searchContact(email) {
    try {
      return await this.request(`/contact?email=${encodeURIComponent(email)}`);
    } catch (error) {
      if (error.response?.status === 404) {
        return { status: "not_found", message: "Contact not found in HubSpot" };
      }
      throw new Error(error.response?.data?.detail || `HTTP ${error.response?.status}: ${error.response?.statusText}`);
    }
  }

  // Create new contact
  async createContact(contact) {
    try {
      return await this.request('/contact', {
        method: 'POST',
        data: contact
      });
    } catch (error) {
      throw new Error(error.response?.data?.detail || `HTTP ${error.response?.status}: ${error.response?.statusText}`);
    }
  }

  // Get all contacts with pagination
  async getAllContacts(limit = 100, after) {
    try {
      const params = new URLSearchParams({ limit: limit.toString() });
      if (after) params.append('after', after);

      return await this.request(`/contacts?${params}`);
    } catch (error) {
      throw new Error(error.response?.data?.detail || `HTTP ${error.response?.status}: ${error.response?.statusText}`);
    }
  }

  /**
   * Create a new HubSpot engagement with customer association
   * 
   * Creates a new engagement (meeting, call, etc.) in HubSpot and associates it 
   * with a customer in the local database.
   * 
   * @param {object} engagement - The engagement data to create
   * @param {string} engagement.engagement_type - The type of engagement (CALL, MEETING, EMAIL, TASK, NOTE)
   * @param {string} engagement.notes - Detailed notes from the engagement
   * @param {string} engagement.contact_id - The HubSpot contact ID (required)
   * @param {string} engagement.customer_id - The customer ID to associate with (required)
   * @param {string} engagement.itinerary_id - The itinerary ID to associate with (optional)
   * @returns {Promise<object>} Response containing the created engagement details
   */
  async createEngagement(engagement) {
    try {
      // Ensure engagement object has all required fields
      if (!engagement.contact_id) {
        throw new Error('contact_id is required for creating an engagement');
      }
      
      // customer_id is now required to link the engagement with a customer
      if (!engagement.customer_id) {
        console.warn('No customer_id provided in createEngagement. The engagement will not be associated with any customer.');
      }
      
      return await this.request('/engagement', {
        method: 'POST',
        data: engagement
      });
    } catch (error) {
      throw new Error(error.response?.data?.detail || `HTTP ${error.response?.status}: ${error.response?.statusText}`);
    }
  }
  
  /**
   * Manually add an existing HubSpot engagement to a customer record
   * 
   * This endpoint is useful when:
   * 1. An engagement was created but not properly associated with a customer
   * 2. You want to link an existing engagement to a different customer
   * 3. You want to ensure the engagement appears in the customer's engagement history
   * 
   * @param {string} customerId - The ID of the customer to associate the engagement with
   * @param {object} engagementData - Object containing engagement details
   * @param {string} engagementData.id - The HubSpot engagement ID
   * @param {string} engagementData.contact_id - The HubSpot contact ID
   * @param {string} engagementData.engagement_type - The type of engagement (default: "MEETING")
   * @param {string} engagementData.notes - The meeting notes
   * @returns {Promise<object>} Response with association status
   * 
   * Note: This uses the /customer/{id}/hubspot/quick-add endpoint
   */
  async associateEngagementWithCustomer(customerId, engagementData) {
    try {
      // Validate that we have the required data
      if (!engagementData.id) {
        throw new Error('Engagement ID is required for associating with customer');
      }
      
      // This endpoint doesn't use the /hubspot prefix
      return await axios({
        url: `${this.apiBaseUrl}/customer/${customerId}/hubspot/quick-add`,
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
          'Content-Type': 'application/json'
        },
        data: {
          id: engagementData.id,
          contact_id: engagementData.contact_id,
          engagement_type: engagementData.engagement_type || 'MEETING',
          notes: engagementData.notes || ''
        }
      }).then(response => response.data);
    } catch (error) {
      throw new Error(error.response?.data?.detail || `HTTP ${error.response?.status}: ${error.response?.statusText}`);
    }
  }

  // Get contact engagements
  async getContactEngagements(contactId) {
    try {
      return await this.request(`/contact/${contactId}/engagements`);
    } catch (error) {
      throw new Error(error.response?.data?.detail || `HTTP ${error.response?.status}: ${error.response?.statusText}`);
    }
  }

  // Helper: Get all contacts using pagination (async generator)
  async *getAllContactsPaginated(limit = 100) {
    let after;

    do {
      const response = await this.getAllContacts(limit, after);
      if (response.status === 'success' && response.data) {
        yield response.data.results;
        after = response.data.paging?.next?.after;
      } else {
        break;
      }
    } while (after);
  }

  // Helper: Search or create contact
  async findOrCreateContact(contactData) {
    try {
      // Try to find existing contact
      const searchResult = await this.searchContact(contactData.email);
      if (searchResult.status === 'success' && searchResult.data) {
        return searchResult.data;
      }
    } catch (error) {
      // Contact not found, continue to create
    }

    // Create new contact
    const createResult = await this.createContact(contactData);
    if (createResult.status === 'success' && createResult.data) {
      return createResult.data;
    }

    throw new Error('Failed to find or create contact');
  }

  // Helper: Get first page of contacts only (for initial load)
  async getFirstPageContacts(limit = 100) {
    try {
      const response = await this.getAllContacts(limit);
      if (response.status === 'success' && response.data) {
        return {
          contacts: response.data.results || [],
          hasMore: !!response.data.paging?.next?.after,
          nextAfter: response.data.paging?.next?.after
        };
      }
      return { contacts: [], hasMore: false, nextAfter: null };
    } catch (error) {
      console.error('Error fetching first page of contacts:', error);
      throw error;
    }
  }

  /**
   * Search HubSpot contacts by various criteria
   * 
   * @param {string} query - Search query term
   * @param {object} options - Search options
   * @param {string[]} options.searchFields - Fields to search in
   * @param {number} options.limit - Number of results per page (1-100)
   * @param {string} options.after - Pagination cursor for next page
   * @returns {Promise<object>} Search results with pagination info
   */
  async searchContacts(query, options = {}) {
    try {
      const params = new URLSearchParams({
        q: query,
        limit: options.limit || 20
      });

      if (options.after) {
        params.append('after', options.after);
      }

      if (options.searchFields && options.searchFields.length > 0) {
        options.searchFields.forEach(field => {
          params.append('search_fields', field);
        });
      }

      return await this.request(`/contacts/search?${params}`);
    } catch (error) {
      throw new Error(error.response?.data?.detail || `HTTP ${error.response?.status}: ${error.response?.statusText}`);
    }
  }

  /**
   * Get customer's past HubSpot engagements with full details
   * 
   * Returns all HubSpot engagements that have been associated with this customer,
   * including contact details and full engagement data from HubSpot.
   * 
   * @param {string} customerId - The ID of the customer to get engagements for
   * @param {boolean} includeDetails - Whether to include full HubSpot engagement details (default: true)
   * @returns {Promise<object>} Response containing customer engagements and details
   */
  async getCustomerEngagements(customerId, includeDetails = true) {
    try {
      const params = new URLSearchParams({ include_details: includeDetails.toString() });
      // Note: Using direct URL without /hubspot prefix since this endpoint is at /customer/{id}/hubspot/engagements
      return await axios({
        url: `${this.apiBaseUrl}/customer/${customerId}/hubspot/engagements?${params}`,
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.getAuthToken()}`,
          'Content-Type': 'application/json'
        }
      }).then(response => response.data);
    } catch (error) {
      throw new Error(error.response?.data?.detail || `HTTP ${error.response?.status}: ${error.response?.statusText}`);
    }
  }
}

export default HubSpotService;